import api from "@/services/api";
import type {
	PaginatedResponse,
	PaginationParams,
	PositionHistoryItem,
	PositionMetricsParams,
	PositionMetricsResponse,
	PositionResponse,
	RouteCoordinate,
	TrackingVehiclesResponse,
	Trip,
} from "../types";

export const getTrip = async (
	vehicleId: number,
	params?: PaginationParams
): Promise<PaginatedResponse<Trip>> => {
	const query = new URLSearchParams();
	if (params?.PageNumber !== undefined) {
		query.append("pageNumber", String(params.PageNumber));
	}
	if (params?.PageSize !== undefined) {
		query.append("pageSize", String(params.PageSize));
	}
	if ((params as PaginationParams & { InitialDate?: string }).InitialDate) {
		query.append(
			"initialDate",
			(params as PaginationParams & { InitialDate?: string })
				.InitialDate as string
		);
	}
	if ((params as PaginationParams & { FinalDate?: string }).FinalDate) {
		query.append(
			"finalDate",
			(params as PaginationParams & { FinalDate?: string }).FinalDate as string
		);
	}
	const ids =
		(params as PaginationParams & { Ids?: number[]; ids?: number[] }).Ids ??
		(params as PaginationParams & { Ids?: number[]; ids?: number[] }).ids;
	if (ids?.length) {
		for (const id of ids) {
			query.append("ids", String(id));
		}
	}

	const response = await api.get<PaginatedResponse<Trip>>(
		`/api/tracking/trip/${vehicleId}${query.toString() ? `?${query.toString()}` : ""}`
	);
	return response.data;
};

export const getPositionPoints = async (
	id: number
): Promise<RouteCoordinate[]> => {
	const response = await api.get<PositionResponse>(
		`/api/tracking/position/${id}`
	);

	return response.data.positions.map((p) => ({
		lat: Number.parseFloat(p.latitude.replace(",", ".")),
		lng: Number.parseFloat(p.longitude.replace(",", ".")),
		speed: p.speed,
		dateTelemetry: p.dateTelemetry,
	}));
};

export const getPositionHistory = async (
	vehicleId: number,
	params?: PaginationParams
): Promise<PaginatedResponse<PositionHistoryItem>> => {
	const response = await api.get<PaginatedResponse<PositionHistoryItem>>(
		`/api/tracking/position/history/${vehicleId}`,
		{ params }
	);
	return response.data;
};

export const getAllPositionHistory = async (
	vehicleId: number,
	InitialDate?: string,
	FinalDate?: string
): Promise<RouteCoordinate[]> => {
	const response = await api.get<PaginatedResponse<PositionHistoryItem>>(
		`/api/tracking/position/history/${vehicleId}`,
		{
			params: {
				PageNumber: 1,
				PageSize: 1000,
				InitialDate,
				FinalDate,
			},
		}
	);

	return response.data.items.map((p) => ({
		lat: Number.parseFloat(p.latitude.replace(",", ".")),
		lng: Number.parseFloat(p.longitude.replace(",", ".")),
	}));
};

export const getPositionMetrics = async (
	vehicleId: number,
	params: PositionMetricsParams
): Promise<PositionMetricsResponse> => {
	const response = await api.get<PositionMetricsResponse>(
		`/api/tracking/position/history/${vehicleId}/metrics`,
		{
			params: {
				initialDate: params.initialDate,
				finalDate: params.finalDate,
			},
		}
	);
	return response.data;
};

export const getTrackingVehicles =
	async (): Promise<TrackingVehiclesResponse> => {
		const response = await api.get<TrackingVehiclesResponse>("/api/tracking");
		return response.data;
	};
